import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Network,
  Building2,
  ListTodo,
  MessageSquareText,
  Calendar,
  Megaphone,
  Hash,
  CalendarDays,
  Settings,
  PhoneCall,
  CalendarCheck,
  Users,
  Wallet,
  Radio,
  Trophy,
  Target,
  ClipboardCheck,
  ClipboardList,
  Calculator,
  FlaskConical,
  BookOpen,
} from 'lucide-react';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { buildQueue } from '@/domain/queueEngine';
import { listPendingOverrideRequests } from '@/domain/approvals';
import { unreadCommsCount } from '@/utils/commsHelpers';
import type { Persona } from '@/domain/types';

type Group = 'studio' | 'queue' | 'bookings' | 'growth' | 'team' | 'accounting' | 'management' | 'system';

// Build status shown as a dot beside each tab. 'ready' = green, safe to test;
// 'wip' = blue, being worked on; 'parked' = red, future work deferred for now.
// Blue (not amber) for wip so it stays easy to tell apart from the red dot.
// Tabs without a status show no dot.
type RailStatus = 'ready' | 'wip' | 'parked';

const STATUS_META: Record<RailStatus, { dot: string; label: string }> = {
  ready: { dot: 'bg-mint', label: 'Ready to test' },
  wip: { dot: 'bg-azure', label: 'Being worked on' },
  parked: { dot: 'bg-blood', label: 'Parked for now' },
};

const STATUS_ORDER: RailStatus[] = ['ready', 'wip', 'parked'];

interface RailLink {
  to: string;
  label: string;
  Icon: typeof Network;
  visible: Persona[];
  group: Group;
  status?: RailStatus;
}

const GROUP_LABELS: Record<Group, string> = {
  studio: 'Studio',
  queue: 'Queue',
  bookings: 'Bookings',
  growth: 'Growth',
  team: 'Team',
  accounting: 'Accounting',
  management: 'Management',
  system: 'System',
};

const GROUP_ORDER: Group[] = [
  'studio',
  'queue',
  'bookings',
  'growth',
  'team',
  'accounting',
  'management',
  'system',
];

// Ordered by the studio-day workflow. STUDIO is the room you're standing in;
// QUEUE is who to contact next; BOOKINGS is the time grid + money-in; GROWTH is
// the pipeline; TEAM is internal; ACCOUNTING is back-office money; MANAGEMENT is
// owner / cross-brand oversight; SYSTEM is demos + settings. Persona gating hides
// empty sections, so each role still sees a short rail.
const LINKS: RailLink[] = [
  // STUDIO — the room you're standing in
  {
    to: '/studio/coburg',
    label: 'Studio',
    Icon: Building2,
    visible: ['georgina', 'studio-manager', 'bookings-agent', 'owner'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/availability',
    label: 'Availability',
    Icon: CalendarCheck,
    visible: ['georgina', 'studio-manager', 'bookings-agent', 'owner'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/floor',
    label: 'Floor',
    Icon: Radio,
    visible: ['georgina', 'studio-manager', 'owner'],
    group: 'studio',
    status: 'ready',
  },
  {
    to: '/payouts',
    label: 'Payouts & cash',
    Icon: Wallet,
    visible: ['georgina', 'studio-manager', 'owner'],
    group: 'studio',
    status: 'ready',
  },

  // QUEUE — who to contact next
  {
    to: '/queue',
    label: 'My queue',
    Icon: ListTodo,
    visible: ['bookings-agent', 'georgina'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/sweeper',
    label: 'Sweeper',
    Icon: Target,
    visible: ['georgina'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/ops',
    label: 'Ops worklist',
    Icon: ClipboardList,
    visible: ['georgina'],
    group: 'queue',
    status: 'wip',
  },
  {
    to: '/confirmations',
    label: 'Confirmations',
    Icon: PhoneCall,
    visible: ['georgina', 'studio-manager'],
    group: 'queue',
    status: 'ready',
  },
  {
    to: '/conversations',
    label: 'Conversations',
    Icon: MessageSquareText,
    visible: ['bookings-agent', 'studio-manager', 'georgina', 'owner'],
    group: 'queue',
    status: 'ready',
  },

  // BOOKINGS — the time grid + money-in
  {
    to: '/calendar/coburg',
    label: 'Calendar',
    Icon: Calendar,
    visible: ['studio-manager', 'bookings-agent', 'georgina'],
    group: 'bookings',
    status: 'ready',
  },
  {
    to: '/deposits',
    label: 'Deposits',
    Icon: Wallet,
    visible: ['bookings-agent', 'studio-manager', 'georgina', 'owner'],
    group: 'bookings',
    status: 'ready',
  },

  // GROWTH — pipeline / CRM
  {
    to: '/customers',
    label: 'Customers',
    Icon: Users,
    visible: ['bookings-agent', 'studio-manager', 'georgina', 'owner'],
    group: 'growth',
    status: 'ready',
  },
  {
    to: '/nurture',
    label: 'Nurture',
    Icon: Users,
    visible: ['georgina', 'owner', 'studio-manager'],
    group: 'growth',
    status: 'parked',
  },
  {
    to: '/future-readiness',
    label: 'Future-readiness',
    Icon: CalendarDays,
    visible: ['georgina', 'bookings-agent', 'studio-manager'],
    group: 'growth',
    status: 'parked',
  },

  // TEAM — internal
  {
    to: '/artists',
    label: 'Artists',
    Icon: Users,
    visible: ['georgina', 'studio-manager', 'owner'],
    group: 'team',
    status: 'ready',
  },
  {
    to: '/comms',
    label: 'Comms',
    Icon: Hash,
    visible: ['georgina', 'studio-manager', 'bookings-agent', 'artist', 'apprentice', 'owner'],
    group: 'team',
    status: 'ready',
  },

  // ACCOUNTING — back-office money (was "Money", amendment 11). The two finance
  // seats (tv-1xc) land here: appending them to the Accounting group only gives
  // them a short finance-only rail, since empty groups are hidden.
  {
    to: '/reconciliation',
    label: 'Reconcile',
    Icon: ClipboardCheck,
    visible: ['georgina', 'owner', 'studio-manager', 'finance', 'finance-manager'],
    group: 'accounting',
    status: 'ready',
  },
  {
    to: '/calculations',
    label: 'Day sheet',
    Icon: Calculator,
    visible: ['georgina', 'owner', 'studio-manager', 'finance', 'finance-manager'],
    group: 'accounting',
    status: 'ready',
  },

  // MANAGEMENT — owner / cross-brand oversight
  {
    to: '/owner',
    label: 'Owner',
    Icon: Megaphone,
    visible: ['georgina', 'owner'],
    group: 'management',
    status: 'wip',
  },
  {
    to: '/network',
    label: 'Network',
    Icon: Network,
    visible: ['georgina', 'owner'],
    group: 'management',
    status: 'wip',
  },
  // /approvals retired (tv-i0b): the no-deposit override queue is now the Ops
  // worklist Overrides tab (georgina-only), and its pending badge moved onto the
  // Ops worklist entry above. Override approval is Ops-only per Diagram 09, so the
  // previous owner access is intentionally dropped (owner gets reports later).
  // Hidden for now — the Reporting concept tab below is the live one. Re-add the
  // LineChart import if you uncomment this.
  // {
  //   to: '/reporting',
  //   label: 'Reporting',
  //   Icon: LineChart,
  //   visible: ['georgina', 'owner', 'studio-manager'],
  //   group: 'management',
  //   status: 'wip',
  // },
  {
    to: '/reporting-concept',
    label: 'Reporting concept',
    Icon: FlaskConical,
    visible: ['georgina', 'owner', 'studio-manager'],
    group: 'management',
    status: 'wip',
  },
  {
    to: '/leaderboard',
    label: 'Leaderboard',
    Icon: Trophy,
    visible: ['georgina', 'owner', 'studio-manager', 'artist'],
    group: 'management',
    status: 'wip',
  },

  // SYSTEM
  {
    to: '/how-it-works',
    label: 'How it works',
    Icon: BookOpen,
    visible: ['georgina', 'studio-manager', 'bookings-agent', 'artist', 'apprentice', 'owner'],
    group: 'system',
    status: 'ready',
  },
  {
    to: '/settings',
    label: 'Settings',
    Icon: Settings,
    visible: ['georgina', 'owner'],
    group: 'system',
  },
];

export default function LeftRail() {
  const persona = useSessionStore((s) => s.persona);
  const agentId = useSessionStore((s) => s.agentId);
  const artistId = useSessionStore((s) => s.artistId);
  const apprenticeId = useSessionStore((s) => s.apprenticeId);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const lastReadByChannel = useSessionStore((s) => s.lastReadByChannel);
  const railCollapsed = useUIStore((s) => s.railCollapsed);
  const customers = useBrainStore((s) => s.customers);
  const bookings = useBrainStore((s) => s.bookings);
  const deposits = useBrainStore((s) => s.deposits);
  const pieces = useBrainStore((s) => s.pieces);
  const artists = useBrainStore((s) => s.artists);
  const internalComms = useBrainStore((s) => s.internalComms);
  const internalDmThreads = useBrainStore((s) => s.internalDmThreads);
  const clockIso = useSimulationStore((s) => s.clockIso);

  const selfPersonId =
    persona === 'owner'
      ? agentId ?? 'owner'
      : persona === 'artist'
        ? artistId
        : persona === 'apprentice'
          ? apprenticeId ?? artistId
          : agentId;

  const commsUnread = useMemo(
    () =>
      unreadCommsCount({
        persona,
        agentId,
        selfPersonId,
        channels: internalComms,
        dms: internalDmThreads,
        lastReadByChannel,
      }),
    [persona, agentId, selfPersonId, internalComms, internalDmThreads, lastReadByChannel],
  );

  const overrideRequests = useBrainStore((s) => s.overrideRequests);
  const approvalsShowForPersona = persona === 'georgina' || persona === 'owner';
  const pendingApprovals = useMemo(
    () => (approvalsShowForPersona ? listPendingOverrideRequests(overrideRequests).length : 0),
    [approvalsShowForPersona, overrideRequests],
  );

  const rebookFollowupTasks = useBrainStore((s) => s.rebookFollowupTasks);
  const queueShowsForPersona = persona === 'bookings-agent' || persona === 'georgina';
  const urgentCount = useMemo(() => {
    if (!queueShowsForPersona) return 0;
    const queue = buildQueue({
      customers,
      bookings,
      deposits,
      pieces,
      artists,
      rebookFollowupTasks,
      studioIds: assignedStudios,
      nowIso: clockIso,
    });
    return queue.filter((q) => q.band === 'now').length;
  }, [
    queueShowsForPersona,
    customers,
    bookings,
    deposits,
    pieces,
    artists,
    rebookFollowupTasks,
    assignedStudios,
    clockIso,
  ]);

  if (!persona) return null;

  // C2: artists/apprentices always have a way back to their own dashboard.
  const selfDashboardTo =
    persona === 'artist' && artistId
      ? `/artist/${artistId}`
      : persona === 'apprentice' && (apprenticeId ?? artistId)
        ? `/apprentice/${apprenticeId ?? artistId}`
        : null;

  const visibleByGroup = new Map<Group, RailLink[]>();
  for (const link of LINKS) {
    if (!link.visible.includes(persona)) continue;
    const arr = visibleByGroup.get(link.group) ?? [];
    arr.push(link);
    visibleByGroup.set(link.group, arr);
  }

  return (
    <nav
      data-testid="left-rail"
      aria-hidden={railCollapsed || undefined}
      className={`shrink-0 overflow-hidden bg-ink-deep transition-[width] duration-200 ease-out ${
        railCollapsed ? 'w-0 border-r-0' : 'w-56 border-r border-gold-dim/15'
      }`}
    >
      <div
        className={`h-full w-56 py-5 px-3 flex flex-col gap-4 overflow-y-auto transition-opacity duration-200 ${
          railCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {selfDashboardTo ? (
          <NavLink
            to={selfDashboardTo}
            data-testid="rail-my-dashboard"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-inter transition ${
                isActive
                  ? 'bg-gold-bright/10 text-gold-bright border border-gold-bright/40'
                  : 'text-cream-soft hover:text-cream hover:bg-ink-soft/60 border border-transparent'
              }`
            }
          >
            <Users size={16} />
            <span className="tracking-wide flex-1">My dashboard</span>
          </NavLink>
        ) : null}
        {GROUP_ORDER.map((group) => {
          const items = visibleByGroup.get(group);
          if (!items || items.length === 0) return null;
          return (
            <section key={group} className="flex flex-col gap-0.5">
              <p className="font-inter text-[9px] uppercase tracking-widest text-gold-dim px-3 mb-1">
                {GROUP_LABELS[group]}
              </p>
              {items.map((l) => {
                const isQueue = l.to === '/queue';
                const isComms = l.to === '/comms';
                // The pending no-deposit override badge lives on the Ops worklist
                // now that /approvals is folded into its Overrides tab (tv-i0b).
                const isOps = l.to === '/ops';
                const badgeCount = isQueue
                  ? urgentCount
                  : isComms
                    ? commsUnread
                    : isOps
                      ? pendingApprovals
                      : 0;
                const badgeTestId = isQueue
                  ? 'queue-urgency-badge'
                  : isComms
                    ? 'comms-unread-badge'
                    : isOps
                      ? 'approvals-pending-badge'
                      : undefined;
                return (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-inter transition ${
                        isActive
                          ? 'bg-gold-bright/10 text-gold-bright border border-gold-bright/40'
                          : 'text-cream-soft hover:text-cream hover:bg-ink-soft/60 border border-transparent'
                      }`
                    }
                  >
                    <l.Icon size={16} />
                    <span className="tracking-wide flex-1">{l.label}</span>
                    {badgeCount > 0 ? (
                      <span
                        data-testid={badgeTestId}
                        className="min-w-[20px] h-5 px-1.5 rounded-full bg-blood text-white text-[10px] font-mono font-bold flex items-center justify-center animate-ring-pulse"
                        title={
                          isQueue
                            ? `${badgeCount} urgent ${badgeCount === 1 ? 'item' : 'items'} in your queue`
                            : isOps
                              ? `${badgeCount} no-deposit ${badgeCount === 1 ? 'request' : 'requests'} awaiting approval`
                              : `${badgeCount} unread ${badgeCount === 1 ? 'message' : 'messages'}`
                        }
                      >
                        {badgeCount > 9 ? '9+' : badgeCount}
                      </span>
                    ) : null}
                    {l.status ? (
                      <span
                        data-testid={`rail-status-${l.status}`}
                        title={STATUS_META[l.status].label}
                        className={`h-2 w-2 shrink-0 rounded-full ${STATUS_META[l.status].dot}`}
                      />
                    ) : null}
                  </NavLink>
                );
              })}
            </section>
          );
        })}
        <section
          data-testid="rail-status-legend"
          className="mt-auto pt-3 border-t border-gold-dim/15 flex flex-col gap-1.5"
        >
          <p className="font-inter text-[9px] uppercase tracking-widest text-gold-dim px-3 mb-0.5">
            Status
          </p>
          {STATUS_ORDER.map((s) => (
            <div key={s} className="flex items-center gap-2.5 px-3">
              <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_META[s].dot}`} />
              <span className="font-inter text-[11px] text-cream-soft tracking-wide">
                {STATUS_META[s].label}
              </span>
            </div>
          ))}
        </section>
      </div>
    </nav>
  );
}
